import produce from 'immer';
import { registerReducer } from '../../../StateSetup/RootReducer';

const { BFF_URL, GBCORE_CLIENT_NAME, GBCORE_CLIENT_VERSION } = window.__ENV__;

// Action names
const ACTION_NAME_PREFIX = 'action/updateFamilyMember';
export const REQUEST_ACTION_NAME = `${ACTION_NAME_PREFIX}/request`;
export const RESPONSE_ACTION_NAME = `${ACTION_NAME_PREFIX}/response`;
export const ERROR_ACTION_NAME = `${ACTION_NAME_PREFIX}/error`;

/**
 * Performs createOrUpdateFamilyMemberCOB mutation to save family member's cob data
 * @param {object} familyMember
 * @param {array} cobs
 * @returns {Promise<object>}
 */
export const UpdateFamilyMember = (familyMember, cobs) => {
    return async (dispatch, getState, { logger }) => {
        // step 1: Request Action
        dispatch({ type: REQUEST_ACTION_NAME });

        const { jwt, sessionCorrelationId, language } = getState().Authentication;
        const transactionId = logger.generateGuid();

        const familyMemberPersonId = familyMember.personId;

        // We only want to run the createOrUpdateFamilyMemberCOBs mutation when the member has selected/deselected a
        // benefit on the UI OR if there's a cobRequired benefit without a cob record
        if (
            Object.keys(familyMember.kindOfCoverageSelected).length > 0 ||
            familyMember.benefits.find((benefit) => benefit.cob === null && benefit.cobRequired === true)
        ) {
            // All cobRequired benefits that are not checked on the UI still need to have a record
            // created for MC if they do not exist already, this code ensures that there will not be
            // any cobRequired benefit with a null cob record when the family member is updated
            const cobBenefitsNotSelected = familyMember.benefits
                .filter(
                    (benefit) =>
                        benefit.cobRequired &&
                        // the benefit must not have an existing cob record OR it must have manually been unchecked on the UI OR benefit(s) checked when there is no other coverage
                        (benefit.cob === null ||
                            (familyMember.kindOfCoverageSelected.hasOwnProperty(benefit.code) &&
                                !familyMember.kindOfCoverageSelected[benefit.code]) ||
                            (familyMember.kindOfCoverageSelected[benefit.code] && !familyMember.hasOtherCoverage)) &&
                        // filter out cobRequired benefits that are selected on the UI and therefore already stored in the cob array
                        cobs.every((cob) => cob.memberBenefitId !== benefit.memberBenefitId)
                )
                .map((benefit) => ({
                    benefitCode: benefit.code,
                    memberBenefitId: benefit.memberBenefitId,
                    cobPolicyNumber: '',
                    cobClaimCertNumber: '',
                    hasOtherInsurer: false,
                    personId: familyMemberPersonId,
                    coveredDependentType: 'INCLUSION',
                }));

            const cobBenefitsSelected = cobs.map((cob) => {
                return {
                    ...cob,
                    // We only want to send a policy number and cert when the insurer is MANULIFE
                    cobPolicyNumber: cob.otherInsurerName === 'MANULIFE' ? cob.cobPolicyNumber : '',
                    cobClaimCertNumber: cob.otherInsurerName === 'MANULIFE' ? cob.cobClaimCertNumber : '',
                    personId: familyMemberPersonId,
                    coveredDependentType: 'INCLUSION',
                };
            });

            const coordinationOfBenefits = [...cobBenefitsSelected, ...cobBenefitsNotSelected];

            logger.info(`${ACTION_NAME_PREFIX}: Updating family Member COBS. (personId: ${familyMember.personId})`, {
                correlation: { transactionId },
            });

            const familyMemberAddCobMutation = await fetch(`${BFF_URL}/api/v2`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'gb-authorization': jwt,
                    'mli-session-correlation-id': sessionCorrelationId,
                    'mli-correlation-id': transactionId,
                    'mli-channel': 'web',
                    'gb-client-name': GBCORE_CLIENT_NAME,
                    'gb-client-version': GBCORE_CLIENT_VERSION,
                    'Accept-Language': language,
                },
                body: JSON.stringify({
                    operationName: 'createOrUpdateFamilyMemberCOBs',
                    variables: {
                        COBs: [...coordinationOfBenefits],
                    },
                    query: `
                    mutation createOrUpdateFamilyMemberCOBs($COBs: [familyMemberCOBInput]){
                        createOrUpdateFamilyMemberCOBs(COBs:$COBs){
                            benefitCode
                            dependentBenefitId
                            memberBenefitId
                            hasOtherInsurer
                            cobPolicyNumber
                            cobClaimCertNumber
                            otherInsurerName
                            coveredDependentType
                        }
                    }
                    `,
                }),
            });

            const familyMemberAddCobMutationResponse = await familyMemberAddCobMutation.json();

            if (familyMemberAddCobMutationResponse.errors) {
                const { message, code } = familyMemberAddCobMutationResponse.errors[0];
                logger.error(
                    `${ACTION_NAME_PREFIX}: Error updating family member cobs records. Code: ${code} Reason: ${message}`,
                    { correlation: { transactionId } }
                );
                return dispatch({
                    type: ERROR_ACTION_NAME,
                    code,
                    message,
                });
            }
        }

        // Once successful response is received from the above mutations, updated family member information is retrieved and response is dispatched and saved in global state(redux).
        return dispatch({
            success: true,
            type: RESPONSE_ACTION_NAME,
        });
    };
};

/**
 * Dispatched before the gb-core-2 requests are sent to indicate loading and reset error state.
 */
export const requestReducer = produce((state) => {
    state.Errors.familyMemberUpdate.hasError = false;
    state.MemberData.isEditFamilyDataSaving = true;
});

/**
 * When we get a successful response, save the family members in the state
 * Reset the isEditFamilyDataSaving is false so the update button is not grey out
 */
export const responseReducer = produce((state) => {
    state.MemberData.isEditFamilyDataSaving = false;
});

/**
 * Called when either call to gb-core-2 fails so we can display an error to the user.
 */
export const errorReducer = produce((state, action) => {
    state.MemberData.isEditFamilyDataSaving = false;
    state.Errors.familyMemberUpdate.hasError = true;
    state.Errors.familyMemberUpdate.code = action.code;
    state.Errors.familyMemberUpdate.message = action.message;
});

// Register Reducers with Redux root reducer
registerReducer(REQUEST_ACTION_NAME, requestReducer);
registerReducer(RESPONSE_ACTION_NAME, responseReducer);
registerReducer(ERROR_ACTION_NAME, errorReducer);
